// Dynamic Sensor Card Loader
// Maps device types to their respective component cards

import React from 'react';
import { DoorSensorCard } from './DoorSensorCard';
import { MotionSensorCard } from './MotionSensorCard';
import { EnergyMeterCard } from './EnergyMeterCard';
import { MetricCard } from './MetricCard';
import { View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

interface SensorCardLoaderProps {
  deviceType: string;
  deviceId: string;
  deviceName: string;
  lastData?: any;
  historicalData?: any[];
  config?: any;
}

export const SensorCardLoader: React.FC<SensorCardLoaderProps> = (props) => {
  const { deviceType } = props;

  // Map device types to components
  switch (deviceType?.toUpperCase()) {
    case 'DOOR':
      return <DoorSensorCard {...props} />;

    case 'MOTION':
      return <MotionSensorCard {...props} />;

    case 'ENERGY':
      return <EnergyMeterCard {...props} />;

    case 'POWER':
      return <PowerMeterCard {...props} />;

    case 'CAMERA':
      return <CameraCard {...props} />;

    case 'ACTUATOR':
      return <ActuatorCard {...props} />;

    // Default: Generic metric display
    default:
      return <GenericSensorCard {...props} />;
  }
};

// Simple implementations for other types
const PowerMeterCard: React.FC<SensorCardLoaderProps> = ({ deviceName, lastData }) => (
  <View style={styles.container}>
    <View style={styles.header}>
      <Ionicons name="flash-outline" size={24} color="#f97316" />
      <Text style={styles.deviceName}>{deviceName}</Text>
    </View>
    <MetricCard
      icon="flash-outline"
      label="Power"
      value={lastData?.power_w?.toFixed(1) || '0'}
      unit="W"
      color="#f97316"
    />
    <MetricCard
      icon="speedometer-outline"
      label="Voltage"
      value={lastData?.voltage_v?.toFixed(1) || '0'}
      unit="V"
      color="#3b82f6"
    />
  </View>
);

const CameraCard: React.FC<SensorCardLoaderProps> = ({ deviceName, lastData }) => (
  <View style={styles.container}>
    <View style={styles.header}>
      <Ionicons name="camera" size={24} color="#6366f1" />
      <Text style={styles.deviceName}>{deviceName}</Text>
    </View>
    <View style={styles.statusBadge}>
      <Ionicons name="videocam" size={32} color="#6366f1" />
      <Text style={styles.statusText}>{lastData?.camera_status || 'Offline'}</Text>
    </View>
  </View>
);

const ActuatorCard: React.FC<SensorCardLoaderProps> = ({ deviceName, lastData }) => (
  <View style={styles.container}>
    <View style={styles.header}>
      <Ionicons name="power" size={24} color="#84cc16" />
      <Text style={styles.deviceName}>{deviceName}</Text>
    </View>
    <View style={styles.statusBadge}>
      <Ionicons
        name={lastData?.actuator_state === 'on' ? 'power' : 'power-outline'}
        size={32}
        color={lastData?.actuator_state === 'on' ? '#84cc16' : '#6b7280'}
      />
      <Text style={styles.statusText}>{lastData?.actuator_state || 'Unknown'}</Text>
    </View>
  </View>
);

const GenericSensorCard: React.FC<SensorCardLoaderProps> = ({
  deviceName,
  deviceType,
  lastData,
}) => (
  <View style={styles.container}>
    <View style={styles.header}>
      <Ionicons name="radio-button-on" size={24} color="#64748b" />
      <Text style={styles.deviceName}>{deviceName}</Text>
      <Text style={styles.deviceType}>({deviceType})</Text>
    </View>
    {/* ✅ FIX: Don't show temperature/humidity/battery here - already shown in Dynamic Current Readings below */}
    {/* Removed duplicate temperature, humidity, battery displays */}
    <View style={styles.placeholderContainer}>
      <Ionicons name="information-circle-outline" size={48} color="#94a3b8" />
      <Text style={styles.placeholderText}>Sensor data shown below</Text>
    </View>
  </View>
);

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    maxHeight: 600,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  deviceName: {
    fontSize: 18,
    fontWeight: '600',
    marginLeft: 12,
    color: '#1f2937',
  },
  deviceType: {
    fontSize: 12,
    color: '#9ca3af',
    marginLeft: 8,
  },
  statusBadge: {
    alignItems: 'center',
    paddingVertical: 24,
  },
  statusText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#374151',
    marginTop: 8,
  },
  placeholderContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 32,
  },
  placeholderText: {
    fontSize: 14,
    color: '#94a3b8',
    marginTop: 12,
    textAlign: 'center',
  },
});
